import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { BaseDialog } from '@/components/common/BaseDialog';
import { ModalIndices } from '@/common/i-doc/i-doc.registry.ts';
import { ConstructionQuestion, CreateQuestionDto, QuestionType } from '@/types/questionnaire-construction';

interface QuestionFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (dto: CreateQuestionDto) => Promise<void>;
  questionTypes: QuestionType[];
  editingQuestion: ConstructionQuestion | null;
  nextOrder: number;
}

const RELATED_ACTION_OPTIONS = [
  { value: '', label: 'Немає' },
  { value: 'updateProfile', label: 'Оновити профіль клієнта' },
  { value: 'updateBodyMeasurement', label: 'Оновити вимірювання тіла' },
];

function emptyForm(nextOrder: number): CreateQuestionDto {
  return {
    typeId: 0,
    name: '',
    order: nextOrder,
    isRequired: false,
    nestCaptionI18n: { uk: '' },
    nestTooltipI18n: { uk: '' },
    nestNoteI18n: { uk: '' },
    nestPlaceholderI18n: { uk: '' },
    relatedAction: '',
  };
}

export const QuestionFormDialog: React.FC<QuestionFormDialogProps> = ({
  open,
  onClose,
  onSave,
  questionTypes,
  editingQuestion,
  nextOrder,
}) => {
  const [form, setForm] = useState<CreateQuestionDto>(emptyForm(nextOrder));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingQuestion) {
      setForm({
        typeId: editingQuestion.typeId,
        name: editingQuestion.name,
        order: editingQuestion.order,
        isRequired: !!editingQuestion.isRequired,
        nestCaptionI18n: { uk: editingQuestion.nestCaptionI18n?.uk ?? '' },
        nestTooltipI18n: { uk: editingQuestion.nestTooltipI18n?.uk ?? '' },
        nestNoteI18n: { uk: editingQuestion.nestNoteI18n?.uk ?? '' },
        nestPlaceholderI18n: { uk: editingQuestion.nestPlaceholderI18n?.uk ?? '' },
        min: editingQuestion.min ?? undefined,
        max: editingQuestion.max ?? undefined,
        relatedAction: editingQuestion.relatedAction ?? '',
      });
    } else {
      setForm(emptyForm(nextOrder));
    }
  }, [open, editingQuestion, nextOrder]);

  const selectedType = questionTypes.find(t => t.id === form.typeId);
  const isNumeric = selectedType?.htmlType === 'number';

  const isValid = form.typeId > 0 && (form.nestCaptionI18n?.uk ?? '').trim().length > 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim() || `question_${Date.now().toString(36)}`,
        relatedAction: form.relatedAction || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={editingQuestion ? 'Редагувати питання' : 'Нове питання'}
      iDoc={ModalIndices.QUIZ_BUILDER_QUESTION_FORM}
      actions={
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Тип питання</InputLabel>
          <Select
            label="Тип питання"
            value={form.typeId || ''}
            onChange={(e) => setForm(f => ({ ...f, typeId: Number(e.target.value) }))}
          >
            {questionTypes.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.comment || t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Ключ питання (унікальна назва поля)"
          value={form.name}
          onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
          fullWidth
          size="small"
          helperText="Латиницею, без пробілів, напр. client_age. Можна залишити порожнім — згенерується автоматично"
        />

        <TextField
          label="Текст питання"
          value={form.nestCaptionI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestCaptionI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
        />

        <TextField
          label="Підказка (tooltip)"
          value={form.nestTooltipI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestTooltipI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
        />

        <TextField
          label="Примітка під питанням"
          value={form.nestNoteI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestNoteI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
        />

        <TextField
          label="Placeholder"
          value={form.nestPlaceholderI18n?.uk ?? ''}
          onChange={(e) => setForm(f => ({ ...f, nestPlaceholderI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
        />

        {isNumeric && (
          <Box display="flex" gap={2}>
            <TextField
              label="Мінімум"
              type="number"
              value={form.min ?? ''}
              onChange={(e) => setForm(f => ({ ...f, min: e.target.value === '' ? undefined : Number(e.target.value) }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Максимум"
              type="number"
              value={form.max ?? ''}
              onChange={(e) => setForm(f => ({ ...f, max: e.target.value === '' ? undefined : Number(e.target.value) }))}
              fullWidth
              size="small"
            />
          </Box>
        )}

        <FormControl fullWidth size="small">
          <InputLabel>Дія при зміні відповіді</InputLabel>
          <Select
            label="Дія при зміні відповіді"
            value={form.relatedAction ?? ''}
            onChange={(e) => setForm(f => ({ ...f, relatedAction: e.target.value }))}
          >
            {RELATED_ACTION_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={!!form.isRequired}
              onChange={(e) => setForm(f => ({ ...f, isRequired: e.target.checked }))}
            />
          }
          label="Обов'язкове питання"
        />
      </Box>
    </BaseDialog>
  );
};
